import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/apiClient';

import {
  getProfessionalProfile,
  getStudentProfile,
  saveProfessionalProfile,
  saveStudentProfile,
  type ProfessionalProfile,
  type ProfessionalProfilePayload,
  type StudentProfile,
  type StudentProfilePayload,
} from './api';

export const STUDENT_PROFILE_KEY = ['profiles', 'student'] as const;
export const PROFESSIONAL_PROFILE_KEY = ['profiles', 'professional'] as const;

/** Mesma política de `useMe`: erro de cliente não se resolve repetindo. */
const retry = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 2;
};

export function useStudentProfile() {
  return useQuery({ queryKey: STUDENT_PROFILE_KEY, queryFn: getStudentProfile, retry });
}

export function useProfessionalProfile() {
  return useQuery({ queryKey: PROFESSIONAL_PROFILE_KEY, queryFn: getProfessionalProfile, retry });
}

/**
 * Grava e guarda a resposta no lugar do que estava em memória.
 *
 * O servidor normaliza — remove repetição, reordena pelo catálogo, apara
 * espaços, transforma o endereço digitado em slug. Descartar a resposta e
 * manter o que foi enviado faria a tela mostrar algo diferente do que ficou
 * gravado, até o próximo recarregamento.
 */
export function useSaveStudentProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: StudentProfilePayload) => saveStudentProfile(payload),
    onSuccess: (saved: StudentProfile) => queryClient.setQueryData(STUDENT_PROFILE_KEY, saved),
  });
}

export function useSaveProfessionalProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfessionalProfilePayload) => saveProfessionalProfile(payload),
    onSuccess: (saved: ProfessionalProfile) =>
      queryClient.setQueryData(PROFESSIONAL_PROFILE_KEY, saved),
  });
}

/**
 * A chave de tradução da recusa.
 *
 * A API responde `domain_rule_violated` com `details.rule` dizendo qual regra
 * esbarrou — `slug_taken`, `slug_reserved`, `bio_required_to_publish`. Uma
 * mensagem só para todas mandaria a pessoa adivinhar o que corrigir.
 */
export function ruleKey(error: unknown): string {
  if (error instanceof ApiError && typeof error.details.rule === 'string') {
    return `profiles:rule.${error.details.rule}`;
  }
  return 'profiles:rule.unknown';
}
